import { useState, useEffect, useCallback, useRef } from "react";
import L from "../libs/leaflet";
import markerMap from "../assets/location_user.gif";
import geoapify from "../utils/geoapify";

/**
 * useLocation (optimizado)
 *
 * ✅ NO rerenderiza continuamente el mapa
 * ✅ Marker imperativo (Leaflet directo)
 * ✅ Live tracking fluido
 * ✅ Cola until map ready
 * ✅ Evita jitter GPS
 * ✅ enableLiveLocation resuelve en el primer fix
 * ✅ disableLiveLocation limpia watcher
 */

const MIN_DISTANCE_METERS = 3;

export default function useLocation(mapRef) {
  // =========================
  // React state (solo UI)
  // =========================
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // =========================
  // Refs (NO reactivos)
  // =========================
  const positionRef = useRef(null);
  const lastPositionRef = useRef(null);

  const watcherIdRef = useRef(null);

  const markerRef = useRef(null);

  const firstFixResolverRef = useRef(null);

  // =========================
  // Queue until map ready
  // =========================
  const pendingRef = useRef([]);

  // =========================
  // Map getter
  // =========================
  const getMap = useCallback(() => {
    return mapRef?.current?.map?.target ?? mapRef?.current ?? null;
  }, [mapRef]);

  // =========================
  // whenMapReady
  // =========================
  const whenMapReady = useCallback(
    (fn) => {
      const map = getMap();

      if (map) {
        fn(map);
        return () => {};
      }

      pendingRef.current.push(fn);

      return () => {
        pendingRef.current = pendingRef.current.filter((x) => x !== fn);
      };
    },
    [getMap],
  );

  // =========================
  // Flush pending
  // =========================
  useEffect(() => {
    const map = getMap();

    if (!map) return;

    const pending = pendingRef.current;

    pendingRef.current = [];

    pending.forEach((fn) => {
      try {
        fn(map);
      } catch (err) {
        console.error("Error en whenMapReady:", err);
      }
    });
  }, [getMap]);

  // =========================
  // Marker helpers
  // =========================
  const ensureMarker = useCallback((map) => {
    if (markerRef.current) {
      return markerRef.current;
    }

    const icon = L.icon({
      iconUrl: markerMap?.src ?? markerMap,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
    });

    const marker = L.marker([0, 0], {
      icon,
    });

    marker.addTo(map);

    markerRef.current = marker;

    return marker;
  }, []);

  const removeMarker = useCallback(() => {
    const map = getMap();

    const marker = markerRef.current;

    if (map && marker) {
      try {
        map.removeLayer(marker);
      } catch (err) {
        console.error("Error removiendo marker:", err);
      }
    }

    markerRef.current = null;
  }, [getMap]);

  const updateMarkerPosition = useCallback(
    (latlng) => {
      whenMapReady((map) => {
        const marker = ensureMarker(map);

        marker.setLatLng(latlng);
      });
    },
    [whenMapReady, ensureMarker],
  );

  // =========================
  // Position helpers
  // =========================
  const updatePosition = useCallback(
    (latlng) => {
      // evitar jitter GPS
      if (
        lastPositionRef.current &&
        lastPositionRef.current.distanceTo(latlng) < MIN_DISTANCE_METERS
      ) {
        return false;
      }

      lastPositionRef.current = latlng;
      positionRef.current = latlng;

      updateMarkerPosition(latlng);

      return true;
    },
    [updateMarkerPosition],
  );

  const getPosition = useCallback(() => {
    return positionRef.current;
  }, []);

  // =========================
  // Disable live tracking
  // =========================
  const disableLiveLocation = useCallback(() => {
    if (watcherIdRef.current != null) {
      navigator.geolocation.clearWatch(watcherIdRef.current);

      watcherIdRef.current = null;
    }

    setLoading(false);

    // cancelar promesa pendiente
    if (firstFixResolverRef.current) {
      try {
        firstFixResolverRef.current.reject?.(
          new Error("Live location cancelada antes del primer fix."),
        );
      } catch (err) {
        console.error("Error cancelando live location:", err);
      }

      firstFixResolverRef.current = null;
    }
  }, []);

  // =========================
  // Enable live tracking
  // =========================
  const enableLiveLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      setError(null);
      setLoading(true);

      if (!navigator.geolocation) {
        const err = new Error(
          "La geolocalización no está soportada en este navegador.",
        );

        setError(err.message);
        setLoading(false);

        reject(err);

        return;
      }

      // limpiar watcher previo
      if (watcherIdRef.current != null) {
        navigator.geolocation.clearWatch(watcherIdRef.current);

        watcherIdRef.current = null;
      }

      // guardar resolver primer fix
      firstFixResolverRef.current = {
        resolve,
        reject,
      };

      const watcherId = navigator.geolocation.watchPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;

          const latlng = L.latLng(latitude, longitude);

          updatePosition(latlng);

          // resolver solo el primer fix
          if (firstFixResolverRef.current) {
            firstFixResolverRef.current.resolve(latlng);

            firstFixResolverRef.current = null;

            setLoading(false);
          }
        },
        (err) => {
          console.error(err);

          setError("No se pudo obtener la ubicación actual.");

          if (firstFixResolverRef.current) {
            firstFixResolverRef.current.reject(err);

            firstFixResolverRef.current = null;
          }

          setLoading(false);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 5000,
          timeout: 10000,
        },
      );

      watcherIdRef.current = watcherId;
    });
  }, [updatePosition]);

  // =========================
  // Single location fix
  // =========================
  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      setError(null);
      setLoading(true);

      if (!navigator.geolocation) {
        const err = new Error(
          "La geolocalización no está soportada en este navegador.",
        );

        setError(err.message);

        setLoading(false);

        reject(err);

        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;

          const latlng = L.latLng(latitude, longitude);

          updatePosition(latlng);

          setLoading(false);

          resolve(latlng);
        },
        (err) => {
          console.error(err);

          setError("No se pudo obtener la ubicación actual.");

          setLoading(false);

          reject(err);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 10000,
        },
      );
    });
  }, [updatePosition]);

  // =========================
  // Center map on user
  // =========================
  const centerMapOnUser = useCallback(
    (zoom = 16) => {
      const latlng = lastPositionRef.current;

      if (!latlng) return;

      whenMapReady((map) => {
        map.setView(latlng, zoom, {
          animate: true,
        });
      });
    },
    [whenMapReady],
  );

  // =========================
  // Toggle marker visibility
  // =========================
  const setMarkerVisible = useCallback(
    (visible) => {
      if (!visible) {
        removeMarker();
        return;
      }

      const latlng = lastPositionRef.current;

      if (!latlng) return;

      updateMarkerPosition(latlng);
    },
    [removeMarker, updateMarkerPosition],
  );

  // =========================
  // Reverse geocode
  // =========================
  const getAddress = useCallback(async (lat, lng) => {
    return geoapify.reverseGeocode(lat, lng);
  }, []);

  // =========================
  // Cleanup
  // =========================
  useEffect(() => {
    return () => {
      disableLiveLocation();

      removeMarker();
    };
  }, [disableLiveLocation, removeMarker]);

  // =========================
  // API
  // =========================
  return {
    // getter no reactivo
    getPosition,

    loading,
    error,

    enableLiveLocation,
    disableLiveLocation,

    getCurrentLocation,

    centerMapOnUser,

    setMarkerVisible,

    getAddress,
  };
}
